import React, { useState } from 'react'
import { FileText, Mail, Newspaper, Palette, Server } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import EmailTemplateManager from './EmailTemplateManager'
import MailBrandingPanel from './MailBrandingPanel'
import NewsletterManager from './NewsletterManager'
import SmtpSettingsPanel from './SmtpSettingsPanel'

interface Props {
  notify?: (message: string, type?: 'success' | 'error' | 'warning' | 'info') => void
}

type Section = 'smtp' | 'branding' | 'templates' | 'newsletters'

const MailCenter: React.FC<Props> = ({ notify }) => {
  const { t } = useTranslation()
  const [section, setSection] = useState<Section>('smtp')
  const sections: Array<{ id: Section; icon: React.ElementType; label: string; description: string }> = [
    { id: 'smtp', icon: Server, label: t('communications.sections.smtp'), description: t('communications.sections.smtpDescription') },
    { id: 'branding', icon: Palette, label: t('communications.sections.branding'), description: t('communications.sections.brandingDescription') },
    { id: 'templates', icon: FileText, label: t('communications.sections.templates'), description: t('communications.sections.templatesDescription') },
    { id: 'newsletters', icon: Newspaper, label: t('communications.sections.newsletters'), description: t('communications.sections.newslettersDescription') }
  ]
  const active = sections.find(item => item.id === section)!

  return <div className="space-y-4">
    <div className="rounded-xl border border-blue-100 bg-gradient-to-r from-blue-50 to-indigo-50 p-4">
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-white p-2 text-blue-600 shadow-sm"><Mail size={18} /></div>
        <div>
          <h3 className="text-sm font-semibold text-slate-900">{t('communications.title')}</h3>
          <p className="mt-1 text-xs text-slate-600">{t('communications.description')}</p>
        </div>
      </div>
    </div>
    <nav className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
      {sections.map(item => {
        const Icon = item.icon
        return <button key={item.id} onClick={() => setSection(item.id)}
          className={`rounded-xl border p-3 text-left transition ${
            section === item.id ? 'border-blue-300 bg-blue-50 shadow-sm' : 'border-gray-200 bg-white hover:border-gray-300'
          }`}>
          <div className="flex items-center gap-2">
            <Icon size={15} className={section === item.id ? 'text-blue-600' : 'text-gray-500'} />
            <span className="text-xs font-semibold">{item.label}</span>
          </div>
          <p className="mt-1 text-[10px] leading-4 text-gray-500">{item.description}</p>
        </button>
      })}
    </nav>
    <div>
      <div className="mb-4">
        <h3 className="text-base font-semibold">{active.label}</h3>
        <p className="mt-0.5 text-xs text-gray-500">{active.description}</p>
      </div>
      {section === 'smtp' && <SmtpSettingsPanel notify={notify} />}
      {section === 'branding' && <MailBrandingPanel notify={notify} />}
      {section === 'templates' && <EmailTemplateManager notify={notify} />}
      {section === 'newsletters' && <NewsletterManager notify={notify} />}
    </div>
  </div>
}

export default MailCenter
